import {
  Breadcrumbs,
  Button,
  Card,
  CardContent,
  CardHeader,
  colors,
  Divider,
  Link,
  Tooltip,
  Table,
  TableBody,
  Typography,
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import clsx from "clsx";
import _ from "lodash";
import PropTypes from "prop-types";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router-dom";

import { fetchEventDefs } from "../actions/eventDefs";
import useCurrentProject from "../hooks/use-project";
import useAccountMetadata from "../hooks/useAccountMetadata";
import { useAllSaasDestinations } from "../hooks/useAllSaasDestinations";
import Navigation from "../Navigation";
import { SET_SINGLE_DESTINATION_CONFIG } from "../reducers/destinations";
import useDestinationById from "../hooks/useDestinationById";
import useFirstDestinationOfType from "../hooks/useFirstDestinationOfType";
import { EventDefsState } from "../reducers/events";
import { StoreState } from "../store";
import {
  loadTransforms,
  organizeTransformations,
} from "../transforms/transformations";
import { LoadedDestinationInfo } from "../types/global";
import { userIsFPEmployee } from "../util";
import {
  DestinationConfigRow,
  DestinationConnectionModeRow,
  DestinationHIPAARow,
  DestinationPHIAllowlistConfigRow,
} from "./configs/shared/ConfigPageRow";
import {
  DestinationRecommendedPropertiesConfigModal,
  DestinationRecommendedPropertiesConfigRow,
} from "./configs/shared/DestinationRecommendedPropertiesConfigRow";
import {
  DestinationRequiredPropertiesConfigModal,
  DestinationRequiredPropertiesConfigRow,
} from "./configs/shared/DestinationRequiredPropertiesConfigRow";
import { DestinationConfigWizard } from "./DestinationConfigWizard";
import { PropertyGroup } from "./DestinationPropertyMetadata";
import { RequestableSaasDestination, SaaSDestination } from "./destinations";
import { ConfigureDestinationsModal } from "./modals/ConfigureDestinationsModal";
import { ConnectionModeDestinationModal } from "./modals/ConnectionModeDestinationModal";
import { HIPAADestinationModal } from "./modals/HIPAADestinationModal";
import { DestinationConfigModalName } from "./types";
import {
  DestinationValidity,
  getDestinationValidity,
  isDestinationFullyConfigured,
  isDestinationPartiallyConfigured,
} from "./validation";
import { DestinationCardTitle } from "../components/DestinationCardTitle";
import { ConfigureImpressionPixelModal } from "./modals/ConfigureImpressionPixelModel";

const useStyles = makeStyles((theme) => ({
  navigation: {
    paddingTop: 12,
  },
  card: {
    width: "50%",
    minWidth: 800,
    minHeight: 500,
  },
  settingsTitle: {
    marginTop: 40,
    marginLeft: 30,
    marginBottom: 20,
  },
  cardContent: {
    padding: 0,
    "&:last-child": {
      paddingBottom: 0,
    },
  },
  body: {
    margin: 24,
  },
  headerAction: {
    marginTop: 0,
  },
  lightText: {
    color: "#546E7A",
  },
  darkText: {
    color: "#263238",
  },
  breadcrumb: {
    marginTop: 5,
    marginBottom: 10,
  },
  totallyInvalidWarning: {
    color: colors.red[600],
    fontSize: "16px",
  },
  partiallyInvalidWarning: {
    color: colors.orange[600],
    fontSize: "16px",
  },
}));

const shouldEventTesterBeDisabled = (
  destination: any,
  configured?: boolean,
  partiallyConfigured?: boolean
) => {
  const isServerSide = destination.supportsServerSide || false;
  const isGooglePubSub = destination?.slug === "google-pubsub";

  if (!isServerSide) {
    return true;
  }

  if (isGooglePubSub) {
    return true;
  }

  if (!configured || !partiallyConfigured) {
    return true;
  }

  return false;
};

function TransformsConfigRow({ destination, transformsExist, transformCount }) {
  const history = useHistory();

  const onOpenConfigModal = () => {
    history.push(`/transformations/destination/${destination.slug}`);
  };

  return (
    <DestinationConfigRow
      name="Event Transformations"
      labelText={
        transformsExist
          ? `${transformCount} transformation${transformCount === 1 ? "" : "s"}`
          : "No transformations"
      }
      onOpenConfigModal={onOpenConfigModal}
    />
  );
}

function EventTesterConfigRow({ destination, disabled, configuredDest }) {
  const history = useHistory();

  const onOpenConfigModal = () => {
    history.push(
      destination.configPageUrl() + "/event-tester/" + configuredDest.id
    );
  };

  return (
    <DestinationConfigRow
      name="Server-Side Event Tester"
      onOpenConfigModal={onOpenConfigModal}
      buttonText="Test Event"
      disabled={disabled}
    />
  );
}

function SendEventsConfigRow({ destination, destinationId }) {
  const history = useHistory();
  const { eventDefs } = useSelector<StoreState, EventDefsState>((state) => ({
    ...state.eventDefs,
  }));

  const eventsUsed = React.useMemo(
    () =>
      eventDefs.filter(
        (ev) =>
          destinationId &&
          (destinationId in ev.enabled_dests || ev.segment_compatible)
      ),
    [eventDefs, destinationId]
  );

  const onConfig = (destinationId: string) =>
    history.push(`/events/library/?destinationsFilter=${destinationId}`);

  return (
    <DestinationConfigRow
      disabled={!destinationId}
      name="Events"
      labelText={
        eventsUsed.length > 0
          ? `Sending ${eventsUsed.length} event${
              eventsUsed.length === 1 ? "" : "s"
            }`
          : "Not sending events"
      }
      onOpenConfigModal={() => onConfig(destinationId)}
    />
  );
}

function ImpressionPixelConfigRow({
  destination,
  configuredDest,
  onOpenConfigModal,
}) {
  const history = useHistory();

  let pixelEnabled =
    configuredDest.settings.freshpaint_pixel_config?.enabled || false;

  return (
    <DestinationConfigRow
      name="Freshpaint Impression Pixel"
      onOpenConfigModal={onOpenConfigModal}
      buttonText="Configure"
      labelText={pixelEnabled ? "Enabled" : ""}
      disabled={configuredDest.id === undefined}
    />
  );
}

function DeliveryMetricsRow({ destination, configuredDest }) {
  const history = useHistory();

  const onOpenConfigModal = () =>
    history.push(
      destination.configPageUrl() + "/delivery-metrics/" + configuredDest.id
    );

  return (
    <DestinationConfigRow
      name="Server-Side Delivery Metrics"
      onOpenConfigModal={onOpenConfigModal}
      buttonText="Metrics"
      disabled={configuredDest.id === undefined}
    />
  );
}

function TracesRow({ destination, configuredDest }) {
  // configuredDest is optional but the button is disabled if it's not configured
  const history = useHistory();

  const onOpenConfigModal = () => {
    history.push(
      `${destination.configPageUrl()}/${configuredDest.id}/event-verification/`
    );
  };

  return (
    <DestinationConfigRow
      name="Server-Side Event Delivery Verification"
      onOpenConfigModal={onOpenConfigModal}
      buttonText="Event Verification"
      disabled={configuredDest.id === undefined}
    />
  );
}

TransformsConfigRow.propTypes = {
  destination: PropTypes.object,
  transformsExist: PropTypes.bool,
  transformCount: PropTypes.number,
  onOpenConfigModal: PropTypes.func,
};

const DefaultDestinationConfigTableBody = ({
  destination,
  onOpenConfigModal,
  transformsExist,
  transformCount,
  configuredDest,
}) => {
  const isFullyConfigured = isDestinationFullyConfigured(
    configuredDest,
    destination
  );
  const isPartiallyConfigured = isDestinationPartiallyConfigured(
    configuredDest,
    destination
  );
  const isNotConfigured = !isFullyConfigured && !isPartiallyConfigured;

  const [disableEventTester, setDisableEventTester] = useState(true);
  useEffect(() => {
    setDisableEventTester(
      shouldEventTesterBeDisabled(
        destination,
        isFullyConfigured,
        isPartiallyConfigured
      )
    );
  }, [isFullyConfigured, isPartiallyConfigured, destination]);

  const accountMetadata = useAccountMetadata();
  const onClickHipaaConfigure = useCallback(() => {
    onOpenConfigModal(DestinationConfigModalName.HIPAA);
  }, [onOpenConfigModal]);

  const onClickConnectionModeConfigure = useCallback(() => {
    onOpenConfigModal(DestinationConfigModalName.ConnectionMode);
  }, [onOpenConfigModal]);

  const onClickRequiredPropertiesConfigure = useCallback(() => {
    onOpenConfigModal(DestinationConfigModalName.RequiredProperties);
  }, [onOpenConfigModal]);

  const onClickRecommendedPropertiesConfigure = useCallback(() => {
    onOpenConfigModal(DestinationConfigModalName.RecommendedProperties);
  }, [onOpenConfigModal]);

  const onClickRecommendedAttributionPropertiesConfigure = useCallback(() => {
    onOpenConfigModal(
      DestinationConfigModalName.RecommendedAttributionProperties
    );
  }, [onOpenConfigModal]);

  const onClickRecommendedViewThroughPropertiesConfigure = useCallback(() => {
    onOpenConfigModal(
      DestinationConfigModalName.RecommendedViewThroughProperties
    );
  }, [onOpenConfigModal]);

  const onClickRecommendedUTMPropertiesConfigure = useCallback(() => {
    onOpenConfigModal(DestinationConfigModalName.RecommendedUTMProperties);
  }, [onOpenConfigModal]);

  const onClickConfigureImpressionPixel = useCallback(() => {
    onOpenConfigModal(DestinationConfigModalName.ConfigureImpressionPixel);
  }, [onOpenConfigModal]);

  if (!accountMetadata.loaded) {
    return null;
  }

  return (
    <TableBody>
      <DestinationConfigRow
        name={destination.configName}
        textColor={isFullyConfigured ? "" : colors.red[600]}
        labelText={
          isFullyConfigured
            ? "Configured"
            : isPartiallyConfigured
            ? "Needs attention"
            : "Not configured"
        }
        onOpenConfigModal={onOpenConfigModal}
      />
      <DestinationHIPAARow
        onOpenConfigModal={onClickHipaaConfigure}
        configuredDestination={configuredDest || {}}
        disabled={isNotConfigured}
      />
      <DestinationRequiredPropertiesConfigRow
        onOpenConfigModal={onClickRequiredPropertiesConfigure}
        destination={destination || {}}
        configuredDestination={configuredDest || {}}
        disabled={isNotConfigured}
      />
      <DestinationRecommendedPropertiesConfigRow
        onOpenConfigModal={onClickRecommendedPropertiesConfigure}
        destination={destination || {}}
        configuredDestination={configuredDest || {}}
        disabled={isNotConfigured}
        propertyGroup={PropertyGroup.Recommended}
        rowName="Recommended Properties"
      />
      <DestinationRecommendedPropertiesConfigRow
        onOpenConfigModal={onClickRecommendedAttributionPropertiesConfigure}
        destination={destination || {}}
        configuredDestination={configuredDest || {}}
        disabled={isNotConfigured}
        propertyGroup={PropertyGroup.Attribution}
        rowName="Recommended Attribution Properties"
      />
      <DestinationRecommendedPropertiesConfigRow
        onOpenConfigModal={onClickRecommendedViewThroughPropertiesConfigure}
        destination={destination || {}}
        configuredDestination={configuredDest || {}}
        disabled={isNotConfigured}
        propertyGroup={PropertyGroup.ViewThrough}
        rowName="Recommended View-Through Properties"
      />
      <DestinationRecommendedPropertiesConfigRow
        onOpenConfigModal={onClickRecommendedUTMPropertiesConfigure}
        destination={destination || {}}
        configuredDestination={configuredDest || {}}
        disabled={isNotConfigured}
        propertyGroup={PropertyGroup.UTM}
        rowName="Recommended UTM Properties"
      />
      <DestinationPHIAllowlistConfigRow
        destination={destination || {}}
        configuredDestination={configuredDest || {}}
        disabled={isNotConfigured}
      />

      {destination.type !== "cohorting" && (
        <DestinationConnectionModeRow
          disabled={isNotConfigured}
          onOpenConfigModal={onClickConnectionModeConfigure}
          configuredDestination={configuredDest || {}}
        />
      )}
      {accountMetadata.config.displayNewEventLibraryPage && (
        <SendEventsConfigRow
          destination={destination || {}}
          destinationId={configuredDest.id}
        />
      )}
      {destination.supportsTrack && destination.type !== "function" && (
        <TransformsConfigRow
          destination={destination}
          transformsExist={transformsExist}
          transformCount={transformCount}
        />
      )}
      {destination.supportsImpressionPixel && (
        <ImpressionPixelConfigRow
          destination={destination}
          configuredDest={configuredDest || {}}
          onOpenConfigModal={onClickConfigureImpressionPixel}
        />
      )}
      {destination.supportsServerSide && destination.type !== "function" && (
        <EventTesterConfigRow
          disabled={disableEventTester}
          destination={destination}
          configuredDest={configuredDest || {}}
        />
      )}
      {destination.supportsServerSide &&
        destination.type !== "function" &&
        userIsFPEmployee() && (
          <DeliveryMetricsRow
            destination={destination}
            configuredDest={configuredDest || {}}
          />
        )}
      {destination.supportsServerSide && destination.type !== "function" && (
        <TracesRow
          destination={destination}
          configuredDest={configuredDest || {}}
        />
      )}
    </TableBody>
  );
};

DefaultDestinationConfigTableBody.propTypes = {
  destination: PropTypes.any,
  onOpenConfigModal: PropTypes.func,
  transformsExist: PropTypes.bool,
  transformCount: PropTypes.number,
  configuredDest: PropTypes.object.isRequired,
};

function CustomDestinationConfigTableBody({
  destination,
  transformsExist,
  transformCount,
  handleUpdateConfigureDest,
  configuredDest,
}) {
  const { customConfig } = destination;
  const [openModal, setOpenModal] = useState<DestinationConfigModalName>();
  const closeModal = () => setOpenModal(undefined);
  const accountMetadata = useAccountMetadata();

  const isFullyConfigured = isDestinationFullyConfigured(
    configuredDest,
    destination
  );
  const isPartiallyConfigured = isDestinationPartiallyConfigured(
    configuredDest,
    destination
  );
  const isNotConfigured = !isFullyConfigured && !isPartiallyConfigured;

  const [disableEventTester, setDisableEventTester] = useState(true);
  useEffect(() => {
    setDisableEventTester(
      shouldEventTesterBeDisabled(
        destination,
        isFullyConfigured,
        isPartiallyConfigured
      )
    );
  }, [isFullyConfigured, isPartiallyConfigured, destination]);

  const hipaaModal =
    openModal === DestinationConfigModalName.HIPAA ? (
      <HIPAADestinationModal
        open={true}
        onClose={closeModal}
        onSave={(_dest, _id, newConfig) => handleUpdateConfigureDest(newConfig)}
        dest={destination}
        existingConfig={configuredDest}
      />
    ) : null;

  const connectionModeModal =
    openModal === DestinationConfigModalName.ConnectionMode ? (
      <ConnectionModeDestinationModal
        open={true}
        onClose={closeModal}
        onSave={(_dest, _id, newConfig) => handleUpdateConfigureDest(newConfig)}
        dest={destination}
        existingConfig={configuredDest}
      />
    ) : null;

  const requiredPropsModal =
    openModal === DestinationConfigModalName.RequiredProperties ? (
      <DestinationRequiredPropertiesConfigModal
        open={true}
        onClose={closeModal}
        onSave={(_dest, _id, newConfig) => handleUpdateConfigureDest(newConfig)}
        dest={destination}
        existingConfig={configuredDest}
      />
    ) : null;

  const recommendedPropsModal =
    openModal === DestinationConfigModalName.RecommendedProperties ? (
      <DestinationRecommendedPropertiesConfigModal
        open={true}
        onClose={closeModal}
        onSave={(_dest, _id, newConfig) => handleUpdateConfigureDest(newConfig)}
        dest={destination}
        existingConfig={configuredDest}
        propertyGroup={PropertyGroup.Recommended}
        title="Allow Recommended Properties"
      />
    ) : null;

  const recommendedAttributionPropsModal =
    openModal ===
    DestinationConfigModalName.RecommendedAttributionProperties ? (
      <DestinationRecommendedPropertiesConfigModal
        open={true}
        onClose={closeModal}
        onSave={(_dest, _id, newConfig) => handleUpdateConfigureDest(newConfig)}
        dest={destination}
        existingConfig={configuredDest}
        propertyGroup={PropertyGroup.Attribution}
        title="Allow Attribution Properties"
      />
    ) : null;

  const recommendedViewThroughPropsModal =
    openModal ===
    DestinationConfigModalName.RecommendedViewThroughProperties ? (
      <DestinationRecommendedPropertiesConfigModal
        open={true}
        onClose={closeModal}
        onSave={(_dest, _id, newConfig) => handleUpdateConfigureDest(newConfig)}
        dest={destination}
        existingConfig={configuredDest}
        propertyGroup={PropertyGroup.ViewThrough}
        title="Allow ViewThrough Properties"
      />
    ) : null;

  const recommendedUTMPropsModal =
    openModal === DestinationConfigModalName.RecommendedUTMProperties ? (
      <DestinationRecommendedPropertiesConfigModal
        open={true}
        onClose={closeModal}
        onSave={(_dest, _id, newConfig) => handleUpdateConfigureDest(newConfig)}
        dest={destination}
        existingConfig={configuredDest}
        propertyGroup={PropertyGroup.UTM}
        title="Allow UTM Properties"
      />
    ) : null;

  const configureImpressionPixelModal =
    openModal === DestinationConfigModalName.ConfigureImpressionPixel ? (
      <ConfigureImpressionPixelModal
        open={true}
        onClose={closeModal}
        onSave={(_dest, _id, newConfig) => handleUpdateConfigureDest(newConfig)}
        dest={destination}
        existingConfig={configuredDest}
      />
    ) : null;

  return (
    <TableBody>
      {customConfig.RowOrder.map((rowName, i) => {
        const RowComponent = customConfig.Rows[rowName];
        const ModalComponent = customConfig.Modals[rowName];
        const setModalOpen = () => setOpenModal(rowName);

        let modal: JSX.Element | null = null;
        if (openModal === rowName) {
          modal = (
            <ModalComponent
              key={`${i}-modal`}
              destination={destination}
              open={openModal === rowName}
              onClose={closeModal}
              handleUpdateConfigureDest={handleUpdateConfigureDest}
              existingConfig={configuredDest}
            />
          );
        }

        return (
          <React.Fragment key={i}>
            <RowComponent
              key={`${i}-row`}
              destination={destination}
              setConfigModalOpen={setModalOpen}
              existingConfig={configuredDest}
            />
            {modal}
          </React.Fragment>
        );
      })}

      <DestinationHIPAARow
        onOpenConfigModal={() => setOpenModal(DestinationConfigModalName.HIPAA)}
        configuredDestination={configuredDest || {}}
        disabled={isNotConfigured}
      />
      {hipaaModal}

      <DestinationRequiredPropertiesConfigRow
        onOpenConfigModal={() =>
          setOpenModal(DestinationConfigModalName.RequiredProperties)
        }
        destination={destination || {}}
        configuredDestination={configuredDest || {}}
        disabled={isNotConfigured}
      />
      {requiredPropsModal}

      <DestinationRecommendedPropertiesConfigRow
        onOpenConfigModal={() =>
          setOpenModal(DestinationConfigModalName.RecommendedProperties)
        }
        destination={destination || {}}
        configuredDestination={configuredDest || {}}
        disabled={isNotConfigured}
        propertyGroup={PropertyGroup.Recommended}
        rowName="Recommended Properties"
      />
      {recommendedPropsModal}

      <DestinationRecommendedPropertiesConfigRow
        onOpenConfigModal={() =>
          setOpenModal(
            DestinationConfigModalName.RecommendedAttributionProperties
          )
        }
        destination={destination || {}}
        configuredDestination={configuredDest || {}}
        disabled={isNotConfigured}
        propertyGroup={PropertyGroup.Attribution}
        rowName="Recommended Attribution Properties"
      />
      {recommendedAttributionPropsModal}

      <DestinationRecommendedPropertiesConfigRow
        onOpenConfigModal={() =>
          setOpenModal(
            DestinationConfigModalName.RecommendedViewThroughProperties
          )
        }
        destination={destination || {}}
        configuredDestination={configuredDest || {}}
        disabled={isNotConfigured}
        propertyGroup={PropertyGroup.ViewThrough}
        rowName="Recommended View-Through Properties"
      />
      {recommendedViewThroughPropsModal}

      <DestinationRecommendedPropertiesConfigRow
        onOpenConfigModal={() =>
          setOpenModal(DestinationConfigModalName.RecommendedUTMProperties)
        }
        destination={destination || {}}
        configuredDestination={configuredDest || {}}
        disabled={isNotConfigured}
        propertyGroup={PropertyGroup.UTM}
        rowName="Recommended UTM Properties"
      />
      {recommendedUTMPropsModal}

      <DestinationPHIAllowlistConfigRow
        destination={destination || {}}
        configuredDestination={configuredDest || {}}
        disabled={isNotConfigured}
      />

      {destination.type !== "cohorting" && (
        <>
          <DestinationConnectionModeRow
            disabled={isNotConfigured}
            onOpenConfigModal={() =>
              setOpenModal(DestinationConfigModalName.ConnectionMode)
            }
            configuredDestination={configuredDest || {}}
          />
          {connectionModeModal}
        </>
      )}

      {accountMetadata.config.displayNewEventLibraryPage && (
        <SendEventsConfigRow
          destination={destination || {}}
          destinationId={configuredDest.id}
        />
      )}

      {destination.supportsTrack && (
        <TransformsConfigRow
          destination={destination}
          transformsExist={transformsExist}
          transformCount={transformCount}
        />
      )}
      {destination.supportsServerSide && (
        <EventTesterConfigRow
          disabled={disableEventTester}
          destination={destination}
          configuredDest={configuredDest || {}}
        />
      )}

      {destination.supportsImpressionPixel && (
        <>
          <ImpressionPixelConfigRow
            destination={destination}
            configuredDest={configuredDest || {}}
            onOpenConfigModal={() => {
              setOpenModal(DestinationConfigModalName.ConfigureImpressionPixel);
            }}
          />
          {configureImpressionPixelModal}
        </>
      )}

      {destination.supportsServerSide && userIsFPEmployee() && (
        <DeliveryMetricsRow
          destination={destination}
          configuredDest={configuredDest || {}}
        />
      )}
      {destination.supportsServerSide && (
        <TracesRow
          destination={destination}
          configuredDest={configuredDest || {}}
        />
      )}
    </TableBody>
  );
}

CustomDestinationConfigTableBody.propTypes = {
  destination: PropTypes.any,
  transformsExist: PropTypes.bool,
  transformCount: PropTypes.number,
  handleUpdateConfigureDest: PropTypes.func,
  settings: PropTypes.object,
  configuredDest: PropTypes.object.isRequired,
};

interface DestinationConfigPageProps {
  destination: SaaSDestination;
  config: LoadedDestinationInfo | undefined;
  onOpenWizard: () => void;
}

function DestinationConfigPage({
  destination,
  config,
  onOpenWizard,
}: DestinationConfigPageProps) {
  const history = useHistory();
  const classes = useStyles();

  const dispatch = useDispatch();
  const configuredDestInState = config;
  const configuredDest = config || ({ settings: {} } as LoadedDestinationInfo);
  const loading = useSelector<StoreState, boolean>(
    (state) => state.destinations.loading
  );
  const [configModalName, setConfigModalName] = useState<
    DestinationConfigModalName
  >();
  const [transformCount, setTransformCount] = useState<number>(0);
  const accountMetadata = useAccountMetadata();
  const currentProject = useCurrentProject();

  const loadTransformCount = () => {
    loadTransforms((transformDefinitions) => {
      if (Array.isArray(transformDefinitions)) {
        const filteredTransformations = organizeTransformations(
          transformDefinitions,
          {
            filter: {
              type: "destinationName",
              value: destination.key,
            },
            includeAll: true,
          }
        );
        setTransformCount(filteredTransformations.length);
      }
    });
  };

  useEffect(loadTransformCount, []);

  if (loading || !accountMetadata.loaded || !currentProject.loaded) {
    return null;
  }

  const setConfiguredDest = (destConfig) => {
    dispatch({
      type: SET_SINGLE_DESTINATION_CONFIG,
      payload: {
        [destConfig.id]: destConfig,
      },
    });
  };

  if (!configuredDest) {
    return null;
  }

  const transformsExist = !loading && transformCount > 0;

  function handleUpdateConfigureDest(destConfig) {
    const { settings } = destConfig;
    const newSettings = _.extend({}, configuredDest.settings || {}, settings);
    const newConfiguredDestState = _.extend(
      {
        mapping: [],
        settings: {},
        event_property_sensitivity_levels: {},
        user_property_sensitivity_levels: {},
        group_property_sensitivity_levels: {},
      },
      configuredDest,
      destConfig,
      {
        settings: newSettings,
      }
    );
    dispatch(fetchEventDefs());
    setConfiguredDest(newConfiguredDestState);
    history.replace(
      destination.configPageUrl() + `/${newConfiguredDestState.id}`
    );
  }

  const onCloseModal = () => setConfigModalName(undefined);
  let modal: JSX.Element | null = null;
  if (configModalName) {
    switch (configModalName) {
      case DestinationConfigModalName.RequiredProperties:
        modal = (
          <DestinationRequiredPropertiesConfigModal
            open={!!configModalName}
            onClose={onCloseModal}
            onSave={(dest, id, destConfig) =>
              handleUpdateConfigureDest(destConfig)
            }
            dest={destination}
            existingConfig={configuredDest}
          />
        );
        break;
      case DestinationConfigModalName.RecommendedProperties:
        modal = (
          <DestinationRecommendedPropertiesConfigModal
            open={!!configModalName}
            onClose={onCloseModal}
            onSave={(dest, id, destConfig) =>
              handleUpdateConfigureDest(destConfig)
            }
            dest={destination}
            existingConfig={configuredDest}
            propertyGroup={PropertyGroup.Recommended}
            title="Allow Recommended Properties"
          />
        );
        break;
      case DestinationConfigModalName.RecommendedAttributionProperties:
        modal = (
          <DestinationRecommendedPropertiesConfigModal
            open={!!configModalName}
            onClose={onCloseModal}
            onSave={(dest, id, destConfig) =>
              handleUpdateConfigureDest(destConfig)
            }
            dest={destination}
            existingConfig={configuredDest}
            propertyGroup={PropertyGroup.Attribution}
            title="Allow Attribution Properties"
          />
        );
        break;
      case DestinationConfigModalName.RecommendedViewThroughProperties:
        modal = (
          <DestinationRecommendedPropertiesConfigModal
            open={!!configModalName}
            onClose={onCloseModal}
            onSave={(dest, id, destConfig) =>
              handleUpdateConfigureDest(destConfig)
            }
            dest={destination}
            existingConfig={configuredDest}
            propertyGroup={PropertyGroup.ViewThrough}
            title="Allow View-Through Properties"
            additionalHelpText={
              <Typography>
                These user properties are used to attribute conversions to
                impressions through view-through conversion modeling and
                enhanced conversions.
              </Typography>
            }
          />
        );
        break;
      case DestinationConfigModalName.RecommendedUTMProperties:
        modal = (
          <DestinationRecommendedPropertiesConfigModal
            open={!!configModalName}
            onClose={onCloseModal}
            onSave={(dest, id, destConfig) =>
              handleUpdateConfigureDest(destConfig)
            }
            dest={destination}
            existingConfig={configuredDest}
            propertyGroup={PropertyGroup.UTM}
            title="Allow UTM Properties"
          />
        );
        break;
      case DestinationConfigModalName.HIPAA:
        modal = (
          <HIPAADestinationModal
            open={!!configModalName}
            onClose={onCloseModal}
            onSave={(dest, id, destConfig) =>
              handleUpdateConfigureDest(destConfig)
            }
            dest={destination}
            existingConfig={configuredDest}
          />
        );
        break;
      case DestinationConfigModalName.ConnectionMode:
        modal = (
          <ConnectionModeDestinationModal
            open={!!configModalName}
            onClose={onCloseModal}
            onSave={(dest, id, destConfig) =>
              handleUpdateConfigureDest(destConfig)
            }
            dest={destination}
            existingConfig={configuredDest}
          />
        );
        break;
      case DestinationConfigModalName.ConfigureImpressionPixel:
        modal = (
          <ConfigureImpressionPixelModal
            open={!!configModalName}
            onClose={onCloseModal}
            onSave={(dest, id, destConfig) =>
              handleUpdateConfigureDest(destConfig)
            }
            dest={destination}
            existingConfig={configuredDest}
          />
        );
        break;
      default:
        modal = (
          <ConfigureDestinationsModal
            includeHeader={true}
            open={!!configModalName}
            onClose={onCloseModal}
            onSave={(dest, id, destConfig) =>
              handleUpdateConfigureDest(destConfig)
            }
            dest={destination}
            settings={configuredDest ? configuredDest.settings : {}}
            existingConfig={configuredDest}
          />
        );
    }
  }

  const destinationValidity = getDestinationValidity(
    accountMetadata,
    currentProject,
    configuredDest,
    destination
  );
  const invalidWarning = destinationValidity.warningMessage;
  let invalidWarningStyle: string;
  if (destinationValidity.validity === DestinationValidity.partiallyInvalid) {
    invalidWarningStyle = classes.partiallyInvalidWarning;
  } else {
    invalidWarningStyle = classes.totallyInvalidWarning;
  }

  let body: JSX.Element | null;
  if (loading) {
    body = null;
  } else if (
    destination.slug === "google-analytics-4-proxy" &&
    !configuredDestInState
  ) {
    body = (
      <CardContent className={clsx(classes.cardContent)}>
        <div className={clsx(classes.body)}>{destination.configBodyHelp}</div>
        <Button
          className={clsx(classes.body)}
          color="primary"
          onClick={onOpenWizard}
          variant="contained"
        >
          Set Up
        </Button>
      </CardContent>
    );
  } else {
    body = (
      <CardContent className={clsx(classes.cardContent)}>
        {invalidWarning && (
          <div className={clsx(classes.body, invalidWarningStyle)}>
            {invalidWarning}
          </div>
        )}
        <div className={clsx(classes.body)}>{destination.configBodyHelp}</div>
        <Divider />
        <Table>
          {destination.customConfig ? (
            <CustomDestinationConfigTableBody
              destination={destination}
              transformsExist={transformsExist}
              transformCount={transformCount}
              handleUpdateConfigureDest={handleUpdateConfigureDest}
              configuredDest={configuredDest}
            />
          ) : (
            !destination.isMobileOnly && (
              <DefaultDestinationConfigTableBody
                destination={destination}
                onOpenConfigModal={setConfigModalName}
                transformsExist={transformsExist}
                transformCount={transformCount}
                configuredDest={configuredDest}
              />
            )
          )}
        </Table>
      </CardContent>
    );
  }

  return (
    <>
      <Card className={clsx(classes.card)}>
        <CardHeader
          title={
            <DestinationCardTitle
              titleSuffix="Configuration Settings"
              destination={destination}
              configuredDest={configuredDest}
            />
          }
          action={
            <Button variant="outlined" onClick={() => history.goBack()}>
              Back
            </Button>
          }
          classes={{ action: clsx(classes.headerAction) }}
        />
        <Divider />
        {body}
      </Card>

      {configModalName && modal}
    </>
  );
}

DestinationConfigPage.propTypes = {
  destination: PropTypes.object.isRequired,
};

interface DestinationConfigPageBreadcrumbsProps {
  destination: SaaSDestination;
}

function DestinationConfigPageBreadcrumbs({
  destination,
}: DestinationConfigPageBreadcrumbsProps) {
  const history = useHistory();
  const classes = useStyles();

  const category = useMemo(() => destination?.categoryDisplay(), [destination]);
  const goToCategory = useCallback(
    () => history.push(`/destinations/${category}`),
    [category, history]
  );

  return (
    <Breadcrumbs
      separator={<NavigateNextIcon fontSize="small" />}
      aria-label="breadcrumb"
      className={clsx(classes.breadcrumb)}
    >
      <Link color="inherit" onClick={() => history.push(`/destinations`)}>
        <Typography className={clsx(classes.lightText)} variant="overline">
          Destinations
        </Typography>
      </Link>
      <Link color="inherit" onClick={goToCategory}>
        <Typography className={clsx(classes.lightText)} variant="overline">
          {destination.categoryDisplay().toUpperCase()}
        </Typography>
      </Link>
      <Typography className={clsx(classes.darkText)} variant="overline">
        {destination.name.toUpperCase()}
      </Typography>
    </Breadcrumbs>
  );
}

const DestinationConfigPageWrapper = ({ destinationSlug, configId }) => {
  const classes = useStyles();
  const allSaasDestinations = useAllSaasDestinations();

  const destination = useMemo(() => allSaasDestinations[destinationSlug], [
    allSaasDestinations,
    destinationSlug,
  ]) as SaaSDestination;

  const destinationById = useDestinationById(configId);
  const firstDestinationOfType = useFirstDestinationOfType(destinationSlug);
  const configuredDestination = useMemo(() => {
    if (destinationById !== undefined) {
      return destinationById;
    }

    return destination.supportsMultiConfig ? undefined : firstDestinationOfType;
  }, [destination, destinationById, firstDestinationOfType]);

  const [isWizardOpen, setWizardOpen] = useState(false);

  if (!destination) {
    return null;
  }

  if (destination instanceof RequestableSaasDestination) {
    return null;
  }

  const isEligibleForWizard =
    !configuredDestination && destinationSlug === "google-analytics-4-proxy";

  return (
    destination && (
      <Navigation className={clsx(classes.navigation)}>
        <DestinationConfigPageBreadcrumbs destination={destination} />
        <DestinationConfigPage
          destination={destination}
          config={configuredDestination}
          onOpenWizard={() => setWizardOpen(true)}
        />
        <DestinationConfigWizard
          destination={destination}
          isOpen={isWizardOpen && isEligibleForWizard}
          onClose={() => setWizardOpen(false)}
        />
      </Navigation>
    )
  );
};

DestinationConfigPageWrapper.propTypes = {
  destinationSlug: PropTypes.string.isRequired,
  configId: PropTypes.string,
};

export default DestinationConfigPageWrapper;
